import {
  AccountResult,
  AccountResultTypenames,
  Query,
  QueryTransferOptionsArgs,
  TransferOptionsResult,
  TransferOptionsResultTypenames,
  TransferSourceInput,
} from '@varo/graphql-schema-types';
import {
  adaptTransferDestinationOptions,
  adaptTransferSourceOptions,
} from 'features/feeBasedInstantTransfer/adapters/transferOptionsAdapter';
import { useTranslation } from 'react-i18next';
import FeeBasedTransferDestinationOptionsQuery from 'services/graphql/queries/MoveMoney/FeeBasedTransferDestinationOptionsQuery';
import FeeBasedTransferSourceOptionsQuery from 'services/graphql/queries/MoveMoney/FeeBasedTransferSourceOptionsQuery';
import { CombinedError, useQuery } from 'urql';

export type TransferOptionsIssue =
  { kind: 'requestFailed' } | { kind: 'blocked'; message?: string };

function getTransferOptionsIssue({
  queryError,
  transferOptionsResult,
  hasUnexpectedAccounts,
}: {
  queryError: CombinedError | undefined;
  transferOptionsResult: TransferOptionsResult | undefined;
  hasUnexpectedAccounts: boolean;
}): TransferOptionsIssue | undefined {
  if (queryError !== undefined) {
    return { kind: 'requestFailed' };
  }
  if (
    transferOptionsResult?.__typename ===
    TransferOptionsResultTypenames.TransferNotPossibleError
  ) {
    return { kind: 'blocked', message: transferOptionsResult.message };
  }
  if (hasUnexpectedAccounts) {
    return { kind: 'blocked' };
  }
  return undefined;
}

function getGroupedAccounts(accountResult: AccountResult | undefined) {
  return accountResult?.__typename === AccountResultTypenames.GroupedAccounts
    ? accountResult
    : undefined;
}

export function useTransferOptions(source: TransferSourceInput | undefined) {
  const { t: translatedText } = useTranslation('feeBasedInstantTransfer', {
    keyPrefix: 'entry',
  });
  const fallbackText = {
    account: translatedText('account'),
    externalAccount: translatedText('externalAccount'),
    formatAvailableBalance: (balance: string) =>
      translatedText('availableBalance', { balance }),
  };
  const [sourceResult, reexecuteSourceQuery] = useQuery<
    Query,
    QueryTransferOptionsArgs
  >({
    query: FeeBasedTransferSourceOptionsQuery,
    pause: source !== undefined,
    requestPolicy: 'network-only',
  });
  const [destinationResult, reexecuteDestinationQuery] = useQuery<
    Query,
    QueryTransferOptionsArgs & {
      sourceAccountId: TransferSourceInput['id'] | undefined;
    }
  >({
    query: FeeBasedTransferDestinationOptionsQuery,
    variables: {
      input: {
        sourceAccountId: source?.id,
        includeExternalDebitCardDestinations: true,
      },
      sourceAccountId: source?.id,
    },
    pause: source === undefined,
    requestPolicy: 'network-only',
  });
  const { data: sourceData, error: sourceQueryError } = sourceResult;
  const { data: destinationData, error: destinationQueryError } =
    destinationResult;
  const isSourceLoading = sourceResult.fetching;
  const isDestinationLoading = destinationResult.fetching;

  const sourceTransferOptions =
    sourceData?.transferOptions.__typename ===
    TransferOptionsResultTypenames.TransferOptions
      ? sourceData.transferOptions
      : undefined;
  const destinationTransferOptions =
    destinationData?.transferOptions.__typename ===
    TransferOptionsResultTypenames.TransferOptions
      ? destinationData.transferOptions
      : undefined;
  const sourceOptions = adaptTransferSourceOptions(
    getGroupedAccounts(sourceTransferOptions?.source),
    fallbackText,
  );
  const groupedDestinationAccounts = getGroupedAccounts(
    destinationTransferOptions?.destination,
  );
  const sourceIssue = isSourceLoading
    ? undefined
    : getTransferOptionsIssue({
        queryError: sourceQueryError,
        transferOptionsResult: sourceData?.transferOptions,
        hasUnexpectedAccounts:
          sourceTransferOptions !== undefined &&
          sourceTransferOptions.source.__typename !==
            AccountResultTypenames.GroupedAccounts,
      });
  const destinationIssue = isDestinationLoading
    ? undefined
    : getTransferOptionsIssue({
        queryError: destinationQueryError,
        transferOptionsResult: destinationData?.transferOptions,
        hasUnexpectedAccounts:
          destinationTransferOptions !== undefined &&
          destinationTransferOptions.destination.__typename !==
            AccountResultTypenames.GroupedAccounts &&
          destinationTransferOptions.destinationExternalDebitCards.length === 0,
      });
  const destinationOptions = adaptTransferDestinationOptions({
    groupedAccounts: groupedDestinationAccounts,
    externalDebitCards:
      destinationTransferOptions?.destinationExternalDebitCards ?? [],
    fallbackText,
  });
  const retrySourceOptions = () =>
    reexecuteSourceQuery({ requestPolicy: 'network-only' });
  const retryDestinationOptions = () =>
    reexecuteDestinationQuery({ requestPolicy: 'network-only' });

  return {
    sourceOptions,
    destinationOptions: isDestinationLoading ? [] : destinationOptions,
    isSourceLoading,
    isDestinationLoading,
    sourceIssue,
    destinationIssue,
    retrySourceOptions,
    retryDestinationOptions,
  };
}
